import { useState, useEffect } from "react";
import { X, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

export function PropertyDialog({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        title: initial?.title || "", price: initial?.price ?? "", location: initial?.location || "",
        address: initial?.address || "", beds: initial?.beds ?? "", baths: initial?.baths ?? "", area: initial?.area ?? "",
        property_type: initial?.property_type || "Residential", status: initial?.status || "For Sale",
        currency: initial?.currency || "US$",
        description: initial?.description || "", features: (initial?.features || []).join("\n"),
        images: initial?.images || [], featured: initial?.featured || false,
        lat: initial?.lat ?? "", lng: initial?.lng ?? "", sold_price: initial?.sold_price ?? "",
        mls: initial?.mls || "", video_url: initial?.video_url || "",
      });
    }
  }, [open, initial]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    const b64s = await Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    })));
    setForm({ ...form, images: [...form.images, ...b64s] });
  };
  const removeImg = (i) => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) });

  const save = () => onSave({
    title: form.title, price: parseInt(form.price) || 0, location: form.location, address: form.address,
    beds: parseInt(form.beds) || 0, baths: parseFloat(form.baths) || 0, area: parseInt(form.area) || 0,
    property_type: form.property_type, status: form.status, description: form.description,
    currency: form.currency,
    features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
    images: form.images, featured: form.featured,
    lat: parseFloat(form.lat) || 0, lng: parseFloat(form.lng) || 0,
    sold_price: parseInt(form.sold_price) || 0,
    mls: form.mls,
    video_url: form.video_url,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-white">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit" : "New"} Listing</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input data-testid="pf-title" value={form.title} onChange={set("title")} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Price</Label><Input data-testid="pf-price" type="number" value={form.price} onChange={set("price")} /></div>
            <div>
              <Label>Currency</Label>
              <select data-testid="pf-currency" value={form.currency} onChange={set("currency")} className="w-full h-10 border border-input rounded-md px-3 text-sm bg-white">
                <option value="US$">US$</option>
                <option value="CI$">CI$</option>
              </select>
            </div>
          </div>
          <div><Label>Location</Label><Input data-testid="pf-location" value={form.location} onChange={set("location")} /></div>
          <div><Label>Full Address</Label><Input value={form.address} onChange={set("address")} /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Beds</Label><Input type="number" value={form.beds} onChange={set("beds")} /></div>
            <div><Label>Baths</Label><Input type="number" step="0.5" value={form.baths} onChange={set("baths")} /></div>
            <div><Label>Area (sqft)</Label><Input type="number" value={form.area} onChange={set("area")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Type</Label><Input value={form.property_type} onChange={set("property_type")} /></div>
            <div><Label>Status</Label><Input value={form.status} onChange={set("status")} placeholder="For Sale / Sold" /></div>
          </div>
          <div><Label>Sold Price (US$, only if Sold)</Label><Input data-testid="pf-soldprice" type="number" value={form.sold_price} onChange={set("sold_price")} placeholder="Leave blank if not sold" /></div>
          <div><Label>MLS #</Label><Input data-testid="pf-mls" value={form.mls} onChange={set("mls")} placeholder="e.g. 419603" /></div>
          <div><Label>Video Tour URL (YouTube, Vimeo, or MP4)</Label><Input data-testid="pf-video" value={form.video_url} onChange={set("video_url")} placeholder="https://youtu.be/…" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Latitude (map)</Label><Input data-testid="pf-lat" type="number" step="any" value={form.lat} onChange={set("lat")} placeholder="e.g. 19.367" /></div>
            <div><Label>Longitude (map)</Label><Input data-testid="pf-lng" type="number" step="any" value={form.lng} onChange={set("lng")} placeholder="e.g. -81.383" /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={set("description")} /></div>
          <div><Label>Features (one per line)</Label><Textarea rows={4} value={form.features} onChange={set("features")} /></div>
          <div>
            <Label>Images</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-20 h-20">
                  <img src={img} alt="" className="w-full h-full object-cover border" />
                  <button onClick={() => removeImg(i)} className="absolute -top-2 -right-2 bg-[#0A1128] text-white rounded-full p-1"><X size={12} /></button>
                </div>
              ))}
              <label className="w-20 h-20 border border-dashed border-[#0A1128]/30 grid place-items-center cursor-pointer hover:bg-[#E8E4D9]/40 transition-colors">
                <Upload size={18} className="text-[#0A1128]/50" />
                <input data-testid="pf-images" type="file" accept="image/*" multiple className="hidden" onChange={addImages} />
              </label>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="pf-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            <Label>Featured on homepage</Label>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t mt-2">
          <button data-testid="property-cancel" onClick={() => onOpenChange(false)} className="px-6 py-3 border border-[#0A1128]/20 text-sm uppercase tracking-wider hover:bg-[#E8E4D9]/40 transition-colors">Cancel</button>
          <button data-testid="property-save" onClick={save} className="px-6 py-3 bg-[#0A1128] text-white text-sm uppercase tracking-wider hover:bg-[#1a2342] transition-colors">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TestimonialDialog({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (open) setForm({ name: initial?.name || "", role: initial?.role || "", quote: initial?.quote || "", photo: initial?.photo || "" });
  }, [open, initial]);
  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const uploadPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setForm({ ...form, photo: r.result }); r.readAsDataURL(f);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit" : "New"} Testimonial</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input data-testid="tf-name" value={form.name} onChange={set("name")} /></div>
          <div><Label>Role</Label><Input data-testid="tf-role" value={form.role} onChange={set("role")} /></div>
          <div><Label>Quote</Label><Textarea data-testid="tf-quote" rows={4} value={form.quote} onChange={set("quote")} /></div>
          <div className="flex items-center gap-3">
            {form.photo && <img src={form.photo} alt="" className="w-14 h-14 object-cover rounded-full" />}
            <label className="px-4 py-2 border border-[#0A1128]/20 text-sm cursor-pointer hover:bg-[#E8E4D9]/40 transition-colors">
              Upload Photo<input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t mt-2">
          <button onClick={() => onOpenChange(false)} className="px-6 py-3 border border-[#0A1128]/20 text-sm uppercase tracking-wider">Cancel</button>
          <button data-testid="testimonial-save" onClick={() => onSave(form)} className="px-6 py-3 bg-[#0A1128] text-white text-sm uppercase tracking-wider hover:bg-[#1a2342] transition-colors">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function OpenHouseDialog({ open, onOpenChange, initial, onSave, properties = [] }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (open) {
      setForm({
        property_id: initial?.property_id || "",
        date: initial?.date || "",
        start_time: initial?.start_time || "",
        end_time: initial?.end_time || "",
        note: initial?.note || "",
      });
    }
  }, [open, initial]);
  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.date && form.start_time;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit" : "New"} Open House</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Property</Label>
            <select data-testid="oh-property" value={form.property_id} onChange={set("property_id")} className="w-full h-10 border border-input rounded-md px-3 text-sm bg-white">
              <option value="">To be announced</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div><Label>Date</Label><Input data-testid="oh-date" type="date" value={form.date} onChange={set("date")} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Start Time</Label><Input data-testid="oh-start" type="time" value={form.start_time} onChange={set("start_time")} /></div>
            <div><Label>End Time</Label><Input data-testid="oh-end" type="time" value={form.end_time} onChange={set("end_time")} /></div>
          </div>
          <div><Label>Note (optional)</Label><Textarea data-testid="oh-note" rows={3} value={form.note} onChange={set("note")} placeholder="e.g. Light refreshments served" /></div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t mt-2">
          <button onClick={() => onOpenChange(false)} className="px-6 py-3 border border-[#0A1128]/20 text-sm uppercase tracking-wider">Cancel</button>
          <button data-testid="oh-save" disabled={!valid} onClick={() => onSave(form)} className="px-6 py-3 bg-[#0A1128] text-white text-sm uppercase tracking-wider hover:bg-[#1a2342] transition-colors disabled:opacity-50">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
